import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Ban, CheckCircle2, KeyRound, PlusCircle, Coins } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [creditModal, setCreditModal] = useState(null);

  const load = async () => {
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/admin/members${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/membership/stats"),
      ]);
      setMembers(list);
      setStats(s);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openMember = async (m) => {
    setSelected(m);
    try {
      const { data } = await api.get(`/admin/members/${m.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (m) => {
    const next = m.status === "blocked" ? "active" : "blocked";
    if (!window.confirm(`${next === "blocked" ? "Block" : "Unblock"} ${m.name}?`)) return;
    try {
      await api.patch(`/admin/members/${m.id}/status`, { status: next });
      toast.success(`Member ${next}`);
      load();
      if (selected?.id === m.id) openMember({ ...m, status: next });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPwd = async (m) => {
    if (!window.confirm(`Reset password for ${m.email}? A new temporary password will be generated.`)) return;
    try {
      const { data } = await api.post("/admin/members/reset-password", { member_id: m.id });
      window.prompt("Copy this new password and share with the member:", data.new_password);
      toast.success("Password reset. Share with member securely.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-members-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Loyalty</div>
          <h1 className="editorial-h1 text-4xl">Members</h1>
        </div>
      </header>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <Stat label="Total" value={stats.total_members} />
          <Stat label="Active" value={stats.active_members} />
          <Stat label="Blocked" value={stats.blocked_members} />
          <Stat label="Coins Issued" value={stats.coins_issued} />
          <Stat label="Coins Spent" value={stats.coins_spent} />
          <Stat label="Outstanding" value={stats.coins_outstanding} accent />
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/45" />
          <input
            data-testid="member-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, email, phone, member code"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-brand-line text-sm"
          />
        </div>
        <button onClick={load} data-testid="member-search-btn" className="btn-pill btn-dark text-xs">Search</button>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="rounded-2xl bg-white border border-brand-line overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-muted text-brand-charcoal/60 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Coins</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-brand-charcoal/50">No members yet.</td></tr>
              )}
              {members.map((m) => (
                <tr key={m.id} className={`border-t border-brand-line hover:bg-brand-muted/40 cursor-pointer ${selected?.id === m.id ? "bg-brand-yellow/10" : ""}`} onClick={() => openMember(m)} data-testid={`member-row-${m.member_code}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.name}</div>
                    <div className="text-[11px] font-mono text-brand-charcoal/55">{m.member_code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-brand-charcoal/75">{m.email}</div>
                    <div className="text-[11px] text-brand-charcoal/50">{m.phone}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">{m.pop_coins ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${m.status === "blocked" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); setCreditModal(m); }} data-testid={`credit-btn-${m.member_code}`} className="text-xs text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1 mr-3">
                      <PlusCircle size={12} /> Credit
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); resetPwd(m); }} data-testid={`reset-btn-${m.member_code}`} className="text-xs text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1 mr-3">
                      <KeyRound size={12} /> Reset
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); toggleStatus(m); }} data-testid={`toggle-btn-${m.member_code}`} className="text-xs text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1">
                      {m.status === "blocked" ? <><CheckCircle2 size={12} /> Activate</> : <><Ban size={12} /> Block</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        {detail ? (
          <aside className="rounded-2xl bg-white border border-brand-line p-6 h-fit sticky top-8">
            <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Member Detail</div>
            <h2 className="font-display text-2xl mt-1">{detail.member.name}</h2>
            <div className="font-mono text-xs text-brand-charcoal/55">{detail.member.member_code}</div>

            <div className="mt-4 rounded-xl bg-brand-yellow/15 p-4 flex items-center gap-3">
              <Coins className="text-brand-charcoal" />
              <div><div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Balance</div><div className="text-2xl font-display leading-none">{detail.balance}</div></div>
            </div>

            <dl className="mt-5 space-y-2 text-sm">
              <Row k="Email" v={detail.member.email} />
              <Row k="Phone" v={detail.member.phone} />
              <Row k="City" v={detail.member.city || "—"} />
              <Row k="Tier" v={<span className="capitalize">{detail.member.tier}</span>} />
              <Row k="Joined" v={new Date(detail.member.created_at).toLocaleDateString()} />
            </dl>

            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Recent Transactions</div>
              <ul className="space-y-2 text-xs max-h-72 overflow-auto">
                {detail.recent_transactions.map((t) => (
                  <li key={t.id} className="flex items-center justify-between border-b border-brand-line py-1">
                    <div className="capitalize">{t.reason.replace(/_/g, " ")}</div>
                    <div className={`font-mono ${t.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>{t.delta > 0 ? "+" : ""}{t.delta}</div>
                  </li>
                ))}
                {detail.recent_transactions.length === 0 && <li className="text-brand-charcoal/45">No transactions yet.</li>}
              </ul>
            </div>
          </aside>
        ) : (
          <aside className="rounded-2xl bg-white border border-brand-line p-6 text-brand-charcoal/50 text-sm text-center">Select a member to view details</aside>
        )}
      </div>

      {creditModal && (
        <CreditCoinsModal
          member={creditModal}
          onClose={() => setCreditModal(null)}
          onSuccess={() => { setCreditModal(null); load(); if (selected) openMember(selected); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-brand-charcoal text-brand-cream border-brand-charcoal" : "bg-white border-brand-line"}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent ? "text-brand-yellow" : "text-brand-charcoal/55"}`}>{label}</div>
      <div className={`text-2xl font-display mt-1 ${accent ? "" : "text-brand-charcoal"}`}>{value ?? 0}</div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex items-center justify-between"><dt className="text-brand-charcoal/55 text-xs uppercase tracking-widest">{k}</dt><dd className="text-right">{v}</dd></div>;
}

function CreditCoinsModal({ member, onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [coins, setCoins] = useState("");
  const [reason, setReason] = useState("purchase");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { member_id: member.id, reason, note };
      if (coins) body.coins = parseInt(coins);
      else if (amount) body.amount_inr = parseFloat(amount);
      else { toast.error("Enter purchase amount or coin count"); setBusy(false); return; }
      const { data } = await api.post("/admin/members/adjust-coins", body);
      toast.success(`Credited ${data.coins_credited} coins. New balance: ${data.new_balance}`);
      onSuccess();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} data-testid="credit-modal" className="w-full max-w-md rounded-2xl bg-white p-6 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Credit Pop Coins</div>
          <h3 className="font-display text-2xl">{member.name}</h3>
          <div className="font-mono text-xs text-brand-charcoal/50">{member.member_code}</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Purchase amount (₹)</label>
          <input type="number" step="0.01" value={amount} onChange={(e) => { setAmount(e.target.value); setCoins(""); }} className="pp-input" placeholder="e.g. 250" data-testid="credit-amount-input" />
        </div>
        <div className="text-center text-xs text-brand-charcoal/45">— OR —</div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Coins (manual override)</label>
          <input type="number" value={coins} onChange={(e) => { setCoins(e.target.value); setAmount(""); }} className="pp-input" placeholder="e.g. 100" data-testid="credit-coins-input" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="pp-input" data-testid="credit-reason-select">
            <option value="purchase">Purchase</option>
            <option value="bonus">Bonus</option>
            <option value="referral">Referral</option>
            <option value="correction">Correction</option>
            <option value="birthday_bonus">Birthday bonus</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Internal note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="pp-input" placeholder="e.g. Bill #1234" data-testid="credit-note-input" />
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-pill btn-ghost text-xs" data-testid="credit-cancel">Cancel</button>
          <button disabled={busy} className="btn-pill btn-dark text-xs" data-testid="credit-submit">{busy ? "Saving…" : "Credit Coins"}</button>
        </div>
      </form>
    </div>
  );
}
